/**
 * @file AgentStatus.jsx
 * @description AgentStatus component for displaying and managing agent status.
 * @component AgentStatus
 * @exports AgentStatus - Main component for displaying and managing agent status.
 */

import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Tabs, Tab, Box, Alert } from "@mui/material";
import { loginRequest } from "../../../authConfig.js";
import { STATUS } from "../../../common/constants/commonConstants.js";
import { WORKER_ERRORS } from "../../../common/constants/ivrModule/workerConstants.js";
import { ASSIGNMENT_ERRORS } from "../../../common/constants/ivrModule/assignmentConstants.js";
import { STATUS_STRINGS, STATUS_TABS, STATUS_ERRORS, STATUS_MESSAGES } from "../../../common/constants/agentModule/statusConstants.js";
import { refreshAgentToken } from "../../../services/ivrModule/acsServices.js";
import { getIvrWorker, updateIvrWorker, disableIvrWorker } from "../../../services/ivrModule/workerServices.js";
import { getIvrAssignment, completeIvrAssignment, closeIvrAssignment } from "../../../services/ivrModule/assignmentServices.js";
import {
    getAgentInfo, getActiveReasons, getActiveSkills,
    getAgentActiveSkills, getAgentOfflineReason, getAgentCurrentStatus,
    getAgentStatusHistory, handleAvailableStatus, handleOfflineStatus
} from "../../../services/agentModule/status/statusServices.js";
import AgentNotes from "./AgentNote.jsx";
import AgentPanel from "./AgentPanel.jsx";
import AgentTexts from "./AgentTexts.jsx";
import AgentCalls from "./AgentCalls.jsx";
import AgentChats from "./AgentChats.jsx";
import AgentEmails from "./AgentEmails.jsx";
import AgentProfile from "./AgentProfile.jsx";
import AgentHistory from "./AgentHistory.jsx";
import { useAgentStatus } from "../../../providers/AgentStatusContext.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";

const AgentStatus = () => {
	const { instance, accounts } = useMsal();
	const account = accounts?.[0];
    const acsIdentity = localStorage.getItem("acsIdentity");
	const ACSIdentityToken = localStorage.getItem("ACSIdentityToken");
    const ACSIdentityExpiration = localStorage.getItem("ACSIdentityExpiration");
	const [profile, setProfile] = useState('');
	const [isLoading, setIsLoading] = useState(0);
    const [ivrWorkers, setIvrWorkers] = useState([]);
	const [userSkills, setUserSkills] = useState([]);
	const [agentStatus, setAgentStatus] = useState('');
	const [userReasons, setUserReasons] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
	const [statusHistory, setStatusHistory] = useState({});
	const [offlineReason, setOfflineReason] = useState('');
	const [selectedSkills, setSelectedSkills] = useState([]);
    const [ivrAssignments, setIvrAssignments] = useState([]);
	const [successMessage, setSuccessMessage] = useState('');
	const [currentTab, setCurrentTab] = useState(STATUS_TABS.PROFILE);
    const [NewACSIdentityToken, setNewACSIdentityToken] = useState(ACSIdentityToken);
    const [NewACSIdentityExpiration, setNewACSIdentityExpiration] = useState(ACSIdentityExpiration);
    const expirationDate = NewACSIdentityExpiration ? new Date(NewACSIdentityExpiration) : null;
	const offlineReasons = userReasons.map(r => r.statereason);
	const startLoading = () => setIsLoading((prev) => prev + 1);
	const stopLoading = () => setIsLoading((prev) => Math.max(prev - 1, 0));
	const showProgress = isLoading > 0;
	const { triggerStatusRefresh } = useAgentStatus();
    const selectedWorker = ivrWorkers.find(ivrWorker => ivrWorker.id === profile?.workerid);

    useEffect(() => {
        fetchAgent();
        fetchReasons();
        fetchWorker();
        fetchAssignment();
    }, []);

	useEffect(() => {
        if (profile?.id) {
            fetchSkills();
            fetchOfflineReason();
            fetchAgentStatus();
            fetchStatusHistory();
        }
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000);
            return () => clearTimeout(timer);
        }
        if (NewACSIdentityExpiration !== null) {
            localStorage.setItem("ACSIdentityExpiration", NewACSIdentityExpiration);
            localStorage.setItem("ACSIdentityToken", NewACSIdentityToken);
        }
    }, [profile, successMessage, NewACSIdentityExpiration]);

    const fetchAgent = async () => {
        try {
            startLoading();
            const tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account });
            const accessToken = tokenResponse.accessToken;
            const user = await getAgentInfo(accessToken);
            let lastNameFormatted = user.lastname;
            if (lastNameFormatted && lastNameFormatted.length <= 2) {
                lastNameFormatted = lastNameFormatted.toUpperCase();
            }
            setProfile(user);
        } catch (error) {
            console.error(STATUS_ERRORS.FETCH_AGENT_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const fetchSkills = async () => {
        try {
            startLoading();
            const allSkills = await getActiveSkills();
            const selected = await getAgentActiveSkills(profile.id);
            const selectedSkillNames = selected.map(skill => skill.skillname);
            setUserSkills(allSkills);
            setSelectedSkills(selectedSkillNames);
        } catch (error) {
            console.error(STATUS_ERRORS.FETCH_ACTIVE_SKILLS_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const fetchReasons = async () => {
        try {
            startLoading();
            const reasons = await getActiveReasons();
            setUserReasons(reasons);
        } catch (error) {
            console.error(STATUS_ERRORS.FETCH_ACTIVE_REASONS_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const fetchAgentStatus = async () => {
        try {
            startLoading();
            const statusData = await getAgentCurrentStatus(profile.id);
            setAgentStatus(statusData?.status === STATUS_STRINGS.ONLINE ? STATUS_STRINGS.AVAILABLE : STATUS_STRINGS.OFFLINE);
        } catch (error) {
            console.error(STATUS_ERRORS.FETCH_AGENT_STATUS_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const fetchOfflineReason = async () => {
        try {
            startLoading();
            const res = await getAgentOfflineReason(profile.id);
            setOfflineReason(res?.statereason || "");
        } catch (err) {
            console.error(STATUS_ERRORS.FETCH_AGENT_OFFLINE_REASON_ERROR, err);
        } finally {
            stopLoading();
        }
    };

    const fetchStatusHistory = async () => {
        try {
            startLoading();
            const history = await getAgentStatusHistory(profile.id);
            const grouped = {};
            history.forEach(row => {
                const date = new Date(row.statusDate).toLocaleDateString();
                if (!grouped[date]) grouped[date] = [];
                grouped[date].push({
                    status: row.statusactive ? STATUS_STRINGS.ONLINE : STATUS_STRINGS.OFFLINE,
                    reason: row.statereason || "",
                    time: new Date(row.statusDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                    skills: row.skills || []
                });
            });
            setStatusHistory(grouped);
        } catch (error) {
            console.error(STATUS_ERRORS.FETCH_AGENT_STATUS_HISTORY_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const fetchWorker = async () => {
        try {
            startLoading();
            const ivrWorkers = await getIvrWorker();
            setIvrWorkers(ivrWorkers);
        } catch (error) {
            console.error(WORKER_ERRORS.FETCH_WORKER_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const fetchAssignment = async () => {
        try {
            startLoading();
            const ivrAssignments = await getIvrAssignment();
            setIvrAssignments(ivrAssignments);
        } catch (error) {
            console.error(ASSIGNMENT_ERRORS.FETCH_ASSIGNMENT_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const handleSkillChange = (skill) => {
        setSelectedSkills(prev =>
            prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
        );
    };

    const handleRefreshToken = async () => {
        try {
            startLoading();
            const data = await refreshAgentToken(acsIdentity);
            if (data?.expiresOn) {
                setNewACSIdentityExpiration(data.expiresOn);
                setNewACSIdentityToken(data.token)
            }
        } catch (err) {
            console.error("Token refresh failed", err);
        } finally {
            stopLoading();
        }
    };

    const handleAvailable = async () => {
        const workerId = profile?.workerid;
        const skillsSelected = userSkills.filter(skill => selectedSkills.includes(skill.skillname));
        const selectedSkillIds = skillsSelected.map(skill => skill.id);
        const selectedQueueIds = skillsSelected.map(skill => skill.queueid).filter(Boolean);
        const channelId = skillsSelected.some(skill => skill.queueid && skill.isVoice)
            ? "voip"
            : skillsSelected.some(skill => skill.queueid && !skill.isVoice)
                ? "chat"
                : "";

        try {
            startLoading();
            if(selectedQueueIds.length > 0) {
                const updateWorkerResponse = await updateIvrWorker({
                    workerId,
                    queues: selectedQueueIds,
                    channelId
                });
                if (updateWorkerResponse?.worker?.communicationError) {
                    const errMsg =
                        updateWorkerResponse.worker.communicationError.details?.[0]?.message ||
                        updateWorkerResponse.worker.communicationError.message ||
                        "Unknown worker update error";
                    setErrorMessage(errMsg);
                    return;
                }
            }
            else {
                await disableIvrWorker({
                    workerId: selectedWorker.id,
                });
            }
            await handleRefreshToken();
            await handleAvailableStatus(profile.id, selectedSkillIds);
            triggerStatusRefresh();
            setSuccessMessage(STATUS_MESSAGES.AGENT_AVAILABILITY_SUCCESS);
            setOfflineReason("");
            await fetchStatusHistory();
        } catch (error) {
            console.error(STATUS_ERRORS.SAVE_AVAILABLE_STATUS_ERROR, error);
        } finally {
            stopLoading();
        }
    };

    const handleOffline = async () => {
        try {
            startLoading();
            const reasonId = userReasons.find(r => r.statereason === offlineReason)?.id;
            await handleOfflineStatus(profile.id, reasonId);
            if (selectedWorker?.assignedJobId) {
                const assignment = ivrAssignments.find(
                    (ivrAssignment) => ivrAssignment.jobid === selectedWorker.assignedJobId && ivrAssignment.workerId === selectedWorker.id
                );
                if (assignment) {
                    await completeIvrAssignment({
                        assignmentId: assignment.id,
                        workerId: assignment.workerId,
                        jobId: assignment.jobid,
                    });
                    await closeIvrAssignment({
                        assignmentId: assignment.id,
                        workerId: assignment.workerId,
                        jobId: assignment.jobid,
                    });
                }
            }
            await disableIvrWorker({
                workerId: selectedWorker.id,
            });
            triggerStatusRefresh();
            setSuccessMessage(STATUS_MESSAGES.AGENT_OFFLINE_SUCCESS);
            setSelectedSkills([]);
            await fetchStatusHistory();
        } catch (error) {
            console.error(STATUS_ERRORS.SAVE_OFFLINE_STATUS_ERROR, error);
        } finally {
            stopLoading();
        }
    };

	const renderTab = () => {
		switch (currentTab) {
			case STATUS_TABS.PROFILE: return <AgentProfile agentStatus={agentStatus} acsIdentity={acsIdentity} expirationDate={expirationDate} handleRefreshToken={handleRefreshToken} profile={profile} />;
			case STATUS_TABS.HISTORY: return <AgentHistory statusHistory={statusHistory}/>;
			case STATUS_TABS.NOTES: return <AgentNotes />;
			case STATUS_TABS.CALLS: return <AgentCalls />;
			case STATUS_TABS.CHATS: return <AgentChats />;
			case STATUS_TABS.TEXTS: return <AgentTexts />;
			case STATUS_TABS.EMAILS: return <AgentEmails />;
			default: return null;
		}
	};

	return (
		<>
            {errorMessage && (
				<>
					<Alert severity={STATUS.ERROR} sx={{ mb: 2, justifyContent: "center" }}>
						{errorMessage}
					</Alert>
				</>
			)}
			{successMessage && (
				<>
					<Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
						{successMessage}
					</Alert>
				</>
			)}
			{showProgress && (
				<>
					<ProgressOverlay />
				</>
			)}
			<Box sx={{ display: "flex", height: "calc(100vh - 150px)" }}>
				<Box sx={{ width: 350, maxWidth: 350, minWidth:350, borderRadius: 3, p:2,
                    background: "linear-gradient(135deg, #fefefe 0%, #f9f9fb 100%)",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease", overflowY: "auto", height: "100%" }}>
					<AgentPanel 
						userSkills={userSkills}
						agentStatus={agentStatus}
						offlineReason={offlineReason}
						handleOffline={handleOffline}
						setAgentStatus={setAgentStatus}
						selectedSkills={selectedSkills}
						offlineReasons={offlineReasons}
						handleAvailable={handleAvailable}
						setOfflineReason={setOfflineReason}
                        setSelectedSkills={setSelectedSkills}
						handleSkillChange={handleSkillChange}
					/>
				</Box>
				<Box sx={{ flexGrow: 1, p: 2, overflowY: "auto" }}>
					<Tabs
						value={currentTab}
						onChange={(e, val) => setCurrentTab(val)}
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab label={STATUS_TABS.PROFILE} value={STATUS_TABS.PROFILE} />
						<Tab label={STATUS_TABS.HISTORY} value={STATUS_TABS.HISTORY} />
						<Tab label={STATUS_TABS.NOTES} value={STATUS_TABS.NOTES} />
						<Tab label={STATUS_TABS.CALLS} value={STATUS_TABS.CALLS} />
						<Tab label={STATUS_TABS.CHATS} value={STATUS_TABS.CHATS} />
						<Tab label={STATUS_TABS.TEXTS} value={STATUS_TABS.TEXTS} />
						<Tab label={STATUS_TABS.EMAILS} value={STATUS_TABS.EMAILS} />
					</Tabs>
					<Box mt={2}>{renderTab()}</Box>
				</Box>
			</Box>
		</>
	);
};

export default AgentStatus;